'use client';

import { forwardRef, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import * as RPNInput from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import es from 'react-phone-number-input/locale/es.json';
import { cn } from '@/shared/utils/cn';

function FlagComponent({ country, countryName }: RPNInput.FlagProps) {
  const Flag = flags[country];
  return (
    <span className="flex h-4 w-6 shrink-0 overflow-hidden rounded-sm bg-adipa-surface-muted [&_svg]:h-full [&_svg]:w-full">
      {Flag && <Flag title={countryName} />}
    </span>
  );
}

const InputComponent = forwardRef<HTMLInputElement, React.ComponentProps<'input'>>(
  function InputComponent({ className, ...props }, ref) {
    return (
      <input
        ref={ref}
        inputMode={props.inputMode ?? 'tel'}
        autoComplete={props.autoComplete ?? 'tel'}
        className={cn(
          'min-w-0 flex-1 bg-transparent px-3 py-2',
          'text-sm text-adipa-text-primary placeholder:text-adipa-text-placeholder',
          'outline-none disabled:cursor-not-allowed',
          className,
        )}
        {...props}
      />
    );
  },
);

type CountryEntry = { label: string; value: RPNInput.Country | undefined };

type CountrySelectProps = {
  disabled?: boolean;
  value: RPNInput.Country;
  options: CountryEntry[];
  onChange: (country: RPNInput.Country) => void;
};

type AnchorRect = { left: number; top: number; width: number };

function CountrySelect({ disabled, value: selectedCountry, options, onChange }: CountrySelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [anchorRect, setAnchorRect] = useState<AnchorRect | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  useEffect(() => {
    if (!open) return;
    setTimeout(() => searchRef.current?.focus(), 0);

    function onOutsideClick(e: MouseEvent) {
      const path = e.composedPath();
      const insideTrigger = containerRef.current && path.includes(containerRef.current);
      const insideDropdown = dropdownRef.current && path.includes(dropdownRef.current);
      if (!insideTrigger && !insideDropdown) {
        setOpen(false);
      }
    }
    function onEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onOutsideClick);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onOutsideClick);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  // El dropdown se portalea a document.body con position fixed atado al rect
  // del campo completo (el root de RPNInput) para escapar de contenedores con
  // overflow (modales, áreas con scroll) — mismo patrón que
  // SearchSuggestionsDropdown.
  useLayoutEffect(() => {
    if (!open) return;

    const updateRect = () => {
      const field = containerRef.current?.parentElement;
      if (!field) return;
      const rect = field.getBoundingClientRect();
      setAnchorRect({ left: rect.left, top: rect.bottom, width: rect.width });
    };

    updateRect();

    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateRect) : null;
    if (observer && containerRef.current?.parentElement) {
      observer.observe(containerRef.current.parentElement);
    }

    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
      observer?.disconnect();
    };
  }, [open]);

  const filtered = options.filter(
    (opt) => opt.value && opt.label.toLowerCase().includes(search.toLowerCase()),
  );

  const handleSelect = (country: RPNInput.Country) => {
    onChange(country);
    setOpen(false);
    setSearch('');
  };

  return (
    <div ref={containerRef} className="shrink-0">
      <button
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-label="Seleccionar país"
        disabled={disabled}
        onClick={() => { setOpen((prev) => !prev); setSearch(''); }}
        className={cn(
          'flex h-full items-center gap-1.5 px-3 py-2',
          'border-r border-adipa-border',
          'rounded-l-adipa-md',
          'text-sm text-adipa-text-primary',
          'hover:bg-adipa-surface-muted transition-colors duration-adipa-fast',
          'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-adipa-focus-ring',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        <FlagComponent country={selectedCountry} countryName={selectedCountry} />

        <svg
          className={cn('h-3 w-3 shrink-0 text-adipa-text-secondary transition-transform duration-adipa-fast', open && 'rotate-180')}
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
        >
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && anchorRect && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            left: anchorRect.left,
            top: anchorRect.top + 4,
            width: anchorRect.width,
            zIndex: 10000,
          }}
          className="overflow-hidden rounded-adipa-md border border-adipa-border bg-adipa-surface shadow-adipa-card"
        >
          <div className="border-b border-adipa-border p-2">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar país..."
              className={cn(
                'w-full rounded-adipa-sm bg-adipa-surface-muted px-3 py-1.5',
                'text-sm text-adipa-text-primary placeholder:text-adipa-text-placeholder',
                'outline-none focus:ring-[3px] focus:ring-adipa-focus-ring',
              )}
            />
          </div>

          <div id={listboxId} role="listbox" className="max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-center text-sm text-adipa-text-secondary">
                No se encontró ningún país.
              </p>
            ) : (
              filtered.map(({ value, label }) =>
                value ? (
                  <button
                    key={value}
                    type="button"
                    role="option"
                    aria-selected={selectedCountry === value}
                    onClick={() => handleSelect(value)}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2',
                      'text-sm text-adipa-text-primary',
                      'hover:bg-adipa-surface-muted transition-colors duration-adipa-fast',
                      selectedCountry === value && 'bg-adipa-surface-lavender font-medium',
                    )}
                  >
                    <FlagComponent country={value} countryName={label} />
                    <span className="flex-1 text-left">{label}</span>
                    <span className="text-xs tabular-nums text-adipa-text-secondary">
                      +{RPNInput.getCountryCallingCode(value)}
                    </span>
                  </button>
                ) : null,
              )
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

function ReadOnlyCountrySelect({ value: selectedCountry }: CountrySelectProps) {
  return (
    <div
      className="flex h-full items-center gap-1.5 rounded-l-adipa-md border-r border-adipa-border px-3 py-2 text-sm text-adipa-text-primary"
      aria-hidden="true"
    >
      <FlagComponent country={selectedCountry} countryName={selectedCountry} />
    </div>
  );
}

export type PhoneInputProps = {
  id?: string;
  value?: RPNInput.Value;
  onChange: (value: RPNInput.Value | undefined) => void;
  onBlur?: () => void;
  defaultCountry?: RPNInput.Country;
  hasError?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  autoComplete?: string;
  className?: string;
  'aria-describedby'?: string;
};

export function PhoneInput({
  id,
  value,
  onChange,
  onBlur,
  defaultCountry = 'CL',
  hasError,
  disabled,
  readOnly,
  placeholder,
  autoComplete,
  className,
  'aria-describedby': ariaDescribedBy,
}: PhoneInputProps) {
  const detectedCountry = value
    ? parsePhoneNumberFromString(String(value))?.country as RPNInput.Country | undefined
    : undefined;
  const readOnlyCountry = readOnly ? detectedCountry ?? defaultCountry : undefined;

  return (
    <RPNInput.default
      id={id}
      value={value || undefined}
      onChange={onChange}
      onBlur={onBlur}
      country={readOnlyCountry}
      defaultCountry={readOnlyCountry ? undefined : defaultCountry}
      international={readOnly ? false : undefined}
      labels={es}
      disabled={disabled}
      readOnly={readOnly}
      placeholder={placeholder}
      autoComplete={autoComplete}
      flagComponent={FlagComponent}
      inputComponent={InputComponent}
      countrySelectComponent={readOnly ? ReadOnlyCountrySelect : CountrySelect}
      smartCaret={false}
      aria-invalid={hasError ? 'true' : undefined}
      aria-describedby={ariaDescribedBy}
      className={cn(
        'relative z-10 flex w-full rounded-adipa-md border bg-adipa-surface',
        'border-adipa-border transition-colors duration-adipa-fast',
        'focus-within:z-50 focus-within:border-adipa-primary focus-within:ring-2 focus-within:ring-adipa-focus-ring',
        hasError &&
          'border-adipa-error focus-within:border-adipa-error focus-within:ring-adipa-error-ring',
        disabled && 'cursor-not-allowed bg-adipa-surface-muted opacity-70',
        readOnly && 'bg-adipa-surface-muted',
        className,
      )}
    />
  );
}
